import camelize from 'camelize';
import type { ICopilotChatMessage } from 'types/copilot';

import { camelizeCopilotPayload } from 'helpers/copilotAgentBuilderDraftIngestion';
import env from 'helpers/env';

/** Relative Copilot paths under `/api/v1/copilot` (same versioning as `actions/copilot.js` and `services/copilot.ts`). */
const buildCopilotV1Url = (path: string) =>
  `${env.avomaApiUrl}/api/v1/copilot${path}`;

export type TCopilotSseEventType =
  | 'chunk'
  | 'completed'
  | 'cancelled'
  | 'name_updated'
  | 'error';

export interface ICopilotRunStartPayload {
  message: string;
  data_json?: Record<string, unknown>;
}

/** Camelized 201 body from POST `/chats/:chatUuid/runs/`. */
export interface ICopilotRunStartSuccess {
  runUuid: string;
  status?: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
}

export interface ICancelledRunPayload {
  runUuid: string;
  status: 'cancelled';
  messages: ICopilotChatMessage[];
}

export interface ICopilotSseParsedEvent {
  id?: string;
  type?: TCopilotSseEventType;
  payload: Record<string, unknown> | null;
  message?: string;
}

export interface ICopilotRunConnectionOptions {
  lastEventId?: string;
  onPayload?: (
    payload: Record<string, unknown>,
    meta: ICopilotSseParsedEvent
  ) => void;
  onCompleted?: (
    payload: Record<string, unknown>,
    meta: ICopilotSseParsedEvent
  ) => void;
  onCancelled?: (
    payload: Record<string, unknown>,
    meta: ICopilotSseParsedEvent
  ) => void;
  onNameUpdated?: (
    payload: Record<string, unknown>,
    meta: ICopilotSseParsedEvent
  ) => void;
  onError?: (error: ICopilotSseParsedEvent) => void;
  onNetworkError?: (event: Event) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface ICopilotRunConnection {
  /**
   * Closes the underlying EventSource. By default this also fires
   * `onClose`, which callers use to detect an unexpected drop and finish
   * the run as failed. Pass `{ silent: true }` for an intentional detach
   * (e.g. the user switched away from this chat) so the run is left alone
   * in Redux/sessionStorage and can be reconnected later.
   */
  close: (closeOptions?: { silent?: boolean }) => void;
  eventsUrl: string;
}

export interface IActiveCopilotRun {
  chatUuid: string;
  runUuid: string;
  lastEventId?: string;
  assistantMessageUuid?: string;
  optimisticUserMessageClientRequestId?: string;
  isCancelPending?: boolean;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
}

/** No initial chunk within this window → local recovery (server stale fail is ~2 min). */
export const COPILOT_INITIAL_NO_RESPONSE_TIMEOUT_MS = 90_000;

const ACTIVE_COPILOT_RUNS_STORAGE_KEY = 'activeCopilotRunsByChatUuid';

export const isActiveCopilotRunStatus = (
  status?: IActiveCopilotRun['status']
): boolean => status === 'pending' || status === 'running';

export const getCopilotChunkWatchdogDelayMs = (
  startedAt?: string,
  nowMs: number = Date.now()
): number => {
  const startedAtMs = startedAt ? new Date(startedAt).getTime() : nowMs;
  const elapsedMs = Math.max(0, nowMs - startedAtMs);
  return Math.max(0, COPILOT_INITIAL_NO_RESPONSE_TIMEOUT_MS - elapsedMs);
};

export const hasCopilotRunStreamProgress = (
  run?: Pick<
    IActiveCopilotRun,
    'lastEventId' | 'assistantMessageUuid' | 'status'
  > | null
): boolean =>
  Boolean(run?.lastEventId || run?.assistantMessageUuid) ||
  run?.status === 'running';

/** Run id from POST / runs/ 201 body (`run_uuid`, `runUuid`, or `uuid`). */
export const getCopilotRunUuid = (
  run?: Record<string, unknown> | null
): string | undefined => {
  if (!run || typeof run !== 'object') {
    return undefined;
  }

  const candidate = run as Record<string, unknown>;
  const runUuid = candidate.runUuid ?? candidate.run_uuid ?? candidate.uuid;

  return typeof runUuid === 'string' && runUuid.length > 0 ? runUuid : undefined;
};

/** Resolves chat uuid on stream payloads (camelized websocket shape or run fallback). */
export const getCopilotStreamChatUuid = (
  payload: Record<string, unknown> | null | undefined,
  fallbackChatUuid?: string
): string | undefined => {
  const dbObject = payload?.dbObject as { uuid?: string } | undefined;
  const dbObjectSnake = payload?.db_object as { uuid?: string } | undefined;
  const chatUuid =
    (typeof dbObject?.uuid === 'string' && dbObject.uuid) ||
    (typeof dbObjectSnake?.uuid === 'string' && dbObjectSnake.uuid) ||
    (typeof payload?.chatUuid === 'string' && payload.chatUuid) ||
    fallbackChatUuid;

  return chatUuid && chatUuid.length > 0 ? chatUuid : undefined;
};

/** Message row from a camelized websocket stream payload (`payload.data`). */
export const getCopilotStreamMessageData = (
  streamPayload: Record<string, unknown> | null | undefined
): Record<string, unknown> | null => {
  if (!streamPayload || typeof streamPayload !== 'object') {
    return null;
  }

  const messageData = streamPayload.data;
  if (
    !messageData ||
    typeof messageData !== 'object' ||
    Array.isArray(messageData)
  ) {
    return null;
  }

  const normalized = camelize(
    messageData as Record<string, unknown>
  ) as Record<string, unknown>;
  const dataJson = normalized.dataJson ?? normalized.data_json;

  return {
    ...normalized,
    ...(dataJson !== undefined && dataJson !== null ? { dataJson } : {})
  };
};

const resolveSseEventType = (
  envelope: Record<string, unknown>,
  event: MessageEvent
): TCopilotSseEventType | string | undefined => {
  const envelopeType = envelope.type;
  if (typeof envelopeType === 'string' && envelopeType.length > 0) {
    return envelopeType;
  }

  if (typeof event.type === 'string' && event.type !== 'message') {
    return event.type;
  }

  return undefined;
};

/**
 * Unwraps the websocket-compatible payload from an SSE `data:` envelope.
 * Backend sends `{ type, payload: "<json string>" }`; the inner JSON holds `db_object` + `data`.
 */
export const unwrapCopilotSseStreamPayload = (
  envelope: unknown
): Record<string, unknown> | null => {
  if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope)) {
    return null;
  }

  const record = envelope as Record<string, unknown>;
  const hasPayloadField = Object.prototype.hasOwnProperty.call(
    record,
    'payload'
  );

  if (hasPayloadField && record.payload != null && record.payload !== '') {
    try {
      const inner =
        typeof record.payload === 'string'
          ? JSON.parse(record.payload)
          : record.payload;

      if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return camelizeCopilotPayload<Record<string, unknown>>(inner);
      }

      return null;
    } catch {
      return null;
    }
  }

  if (record.msg === 'stream' || record.msg === 'copilot_name_updated') {
    return camelizeCopilotPayload<Record<string, unknown>>(record);
  }

  if (record.status === 'cancelled' && Array.isArray(record.messages)) {
    return camelizeCopilotPayload<Record<string, unknown>>(record);
  }

  return null;
};

export const startCopilotRun = async (
  chatUuid: string,
  payload: ICopilotRunStartPayload
): Promise<ICopilotRunStartSuccess> => {
  const runsUrl = buildCopilotV1Url(`/chats/${chatUuid}/runs/`);

  const response = await fetch(runsUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  const responseText = await response.text();
  let json: unknown = null;

  if (responseText) {
    try {
      json = JSON.parse(responseText);
    } catch {
      json = responseText;
    }
  }

  if (!response.ok) {
    const error = new Error(response.statusText || 'Copilot run failed') as Error & {
      status?: number;
      data?: unknown;
    };
    error.status = response.status;
    error.data = json;
    throw error;
  }

  const camelized = camelize(json as Record<string, unknown>) as Record<
    string,
    unknown
  >;
  const runUuid = getCopilotRunUuid(camelized);

  if (!runUuid) {
    throw new Error('Missing Copilot run UUID');
  }

  const { status } = camelized;

  return {
    runUuid,
    ...(status === 'pending' ||
    status === 'running' ||
    status === 'completed' ||
    status === 'failed' ||
    status === 'cancelled'
      ? { status }
      : {})
  };
};

const parseJsonResponse = async (response: Response): Promise<unknown> => {
  const responseText = await response.text();
  if (!responseText) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return responseText;
  }
};

const normalizeCopilotMessageAuthor = (
  author: unknown
): ICopilotChatMessage['author'] => {
  if (author === 'copilot') {
    return 0;
  }
  if (author === 'user') {
    return 1;
  }
  if (author === 'system') {
    return 2;
  }
  return typeof author === 'number' ? author : 0;
};

export const normalizeCancelledRunPayload = (
  payload: unknown,
  fallbackRunUuid?: string
): ICancelledRunPayload | null => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }

  const normalized = camelize(
    payload as Record<string, unknown>
  ) as Record<string, unknown>;
  const payloadRunUuid = getCopilotRunUuid(normalized);
  const runUuid = payloadRunUuid ?? fallbackRunUuid;
  const { messages } = normalized;

  if (
    !runUuid ||
    (payloadRunUuid && fallbackRunUuid && payloadRunUuid !== fallbackRunUuid) ||
    normalized.status !== 'cancelled' ||
    !Array.isArray(messages)
  ) {
    return null;
  }

  return {
    runUuid,
    status: 'cancelled',
    messages: messages
      .filter(
        (message): message is Record<string, unknown> =>
          Boolean(message) &&
          typeof message === 'object' &&
          !Array.isArray(message)
      )
      .map(message => {
        const normalizedMessage = camelize(message) as Record<string, unknown>;
        return {
          ...normalizedMessage,
          author: normalizeCopilotMessageAuthor(normalizedMessage.author)
        } as unknown as ICopilotChatMessage;
      })
  };
};

export class CopilotRunCancelError extends Error {
  status: number;

  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'CopilotRunCancelError';
    this.status = status;
    this.data = data;
  }
}

export const cancelCopilotRun = async (
  runUuid: string
): Promise<ICancelledRunPayload> => {
  const cancelUrl = buildCopilotV1Url(`/stream-runs/${runUuid}/cancel/`);
  const response = await fetch(cancelUrl, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' }
  });
  const data = await parseJsonResponse(response);
  const camelizedData =
    data && typeof data === 'object' && !Array.isArray(data)
      ? (camelize(data as Record<string, unknown>) as Record<string, unknown>)
      : data;

  if (!response.ok) {
    throw new CopilotRunCancelError(
      response.statusText || 'Copilot run cancellation failed',
      response.status,
      camelizedData
    );
  }

  const cancelledRun = normalizeCancelledRunPayload(camelizedData);
  if (!cancelledRun || cancelledRun.runUuid !== runUuid) {
    throw new CopilotRunCancelError(
      'Copilot run cancellation returned an invalid payload',
      response.status,
      camelizedData
    );
  }
  return cancelledRun;
};

export const buildCopilotRunEventsUrl = (
  runUuid: string,
  lastEventId?: string
) => {
  const baseUrl = buildCopilotV1Url(`/stream-runs/${runUuid}/events/`);

  if (!lastEventId) {
    return baseUrl;
  }

  return `${baseUrl}?last_event_id=${encodeURIComponent(lastEventId)}`;
};

export const parseCopilotSseEvent = (
  event: MessageEvent
): ICopilotSseParsedEvent => {
  const envelope = JSON.parse(event.data) as Record<string, unknown>;

  return {
    id: event.lastEventId,
    type: resolveSseEventType(envelope, event) as TCopilotSseEventType,
    payload: unwrapCopilotSseStreamPayload(envelope),
    message:
      typeof envelope.message === 'string' ? envelope.message : undefined
  };
};

/**
 * Opens the Copilot run event stream via native `EventSource` (not fetch/ReadableStream).
 * Uses `withCredentials: true` so session cookies authenticate the GET without custom headers.
 * The browser delivers each SSE frame to listeners as it arrives; handlers run per event.
 */
export const connectCopilotRun = (
  runUuid: string,
  options: ICopilotRunConnectionOptions
): ICopilotRunConnection => {
  const eventsUrl = buildCopilotRunEventsUrl(runUuid, options.lastEventId);

  const source = new EventSource(eventsUrl, { withCredentials: true });

  const handleStreamEvent = (event: Event) => {
    if (!('data' in event) || !(event as MessageEvent).data) {
      return;
    }

    try {
      const parsedEvent = parseCopilotSseEvent(event as MessageEvent);
      const eventType =
        parsedEvent.type ||
        (event as Event & { type?: string }).type ||
        undefined;

      switch (eventType) {
        case 'chunk':
          if (parsedEvent.payload) {
            options.onPayload?.(parsedEvent.payload, parsedEvent);
          }
          break;
        case 'completed':
          if (parsedEvent.payload) {
            options.onCompleted?.(parsedEvent.payload, parsedEvent);
          }
          break;
        case 'cancelled':
          if (parsedEvent.payload) {
            options.onCancelled?.(parsedEvent.payload, parsedEvent);
          }
          break;
        case 'name_updated':
          if (parsedEvent.payload) {
            options.onNameUpdated?.(parsedEvent.payload, parsedEvent);
          }
          break;
        case 'error':
          options.onError?.(parsedEvent);
          break;
        default:
          break;
      }
    } catch {
      // Ignore malformed SSE frames.
    }
  };

  // Named SSE events (`event: chunk`, etc.) and default `message` events.
  source.addEventListener('chunk', handleStreamEvent);
  source.addEventListener('completed', handleStreamEvent);
  source.addEventListener('cancelled', handleStreamEvent);
  source.addEventListener('name_updated', handleStreamEvent);
  source.addEventListener('error', handleStreamEvent);
  source.onmessage = handleStreamEvent;
  source.onopen = () => options.onOpen?.();
  source.onerror = event => options.onNetworkError?.(event);

  return {
    close: closeOptions => {
      source.close();
      if (!closeOptions?.silent) {
        options.onClose?.();
      }
    },
    eventsUrl
  };
};

const canUseSessionStorage = () => typeof window !== 'undefined';

export const loadStoredCopilotRuns = (): Record<string, IActiveCopilotRun> => {
  if (!canUseSessionStorage()) {
    return {};
  }

  try {
    const stored = window.sessionStorage.getItem(
      ACTIVE_COPILOT_RUNS_STORAGE_KEY
    );
    if (!stored) {
      return {};
    }

    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const saveStoredCopilotRun = (run: IActiveCopilotRun) => {
  if (!canUseSessionStorage()) {
    return;
  }

  const runs = loadStoredCopilotRuns();
  runs[run.chatUuid] = run;
  try {
    window.sessionStorage.setItem(
      ACTIVE_COPILOT_RUNS_STORAGE_KEY,
      JSON.stringify(runs)
    );
  } catch {
    // Ignore restricted or quota-limited sessionStorage failures.
  }
};

export const removeStoredCopilotRun = (chatUuid: string) => {
  if (!canUseSessionStorage()) {
    return;
  }

  const runs = loadStoredCopilotRuns();
  delete runs[chatUuid];
  try {
    window.sessionStorage.setItem(
      ACTIVE_COPILOT_RUNS_STORAGE_KEY,
      JSON.stringify(runs)
    );
  } catch {
    // Ignore restricted or quota-limited sessionStorage failures.
  }
};

export const createCopilotRunEventDeduper = () => {
  const processedEventIdsByRunUuid: Record<string, Set<string>> = {};

  return (runUuid: string, eventId?: string) => {
    if (!eventId) {
      return true;
    }

    if (!processedEventIdsByRunUuid[runUuid]) {
      processedEventIdsByRunUuid[runUuid] = new Set();
    }

    if (processedEventIdsByRunUuid[runUuid].has(eventId)) {
      return false;
    }

    processedEventIdsByRunUuid[runUuid].add(eventId);
    return true;
  };
};
